/**
 * Table des états d'interaction (US-25).
 *
 * Constat d'origine : toute la feuille de style ne contenait **qu'une seule**
 * règle liée à un état — un `:focus-visible` universel, correct mais seul.
 * Aucun `:hover`, aucun `:active` systématique, rien pour « désactivé » au-delà
 * d'une opacité, rien pour « en cours ». Un contrôle qui ne réagit pas au
 * survol se lit comme une décoration ; un bouton qui ne dit pas qu'il travaille
 * se fait cliquer deux fois.
 *
 * Les six états, et leur signal :
 *
 * | État        | Signal |
 * | ----------- | ------ |
 * | repos       | la couleur de fond ou la bordure du composant |
 * | survol      | teinte de survol du fond, bordure en accent, ou surface qui apparaît |
 * | focus       | anneau de focus visible, décollé du contrôle |
 * | pressé      | tassement à 0,97 (déjà posé sur `Button`) |
 * | désactivé   | opacité 40 % et pointeur neutralisé |
 * | chargement  | curseur d'attente, contrôle inerte, libellé conservé |
 *
 * Ces fragments sont des classes Tailwind partagées et non un composant : ils
 * s'appliquent à des éléments de natures différentes (bouton, champ, case,
 * lien) qui n'ont en commun que leurs réactions.
 */

/**
 * Anneau de focus. `focus-visible` et non `focus` : un clic à la souris ne doit
 * pas laisser un anneau derrière lui. `ring-offset` le décolle du contrôle,
 * sans quoi il se confond avec la bordure sur fond sombre — le défaut relevé
 * sur l'anneau générique d'origine.
 */
export const FOCUS_RING =
  'outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

/**
 * Survol d'un contrôle plein (bouton primaire) : le fond passe à la teinte
 * de survol de la marque.
 *
 * **Ce n'était pas le cas.** `hover:brightness-95` a servi de survol à tous
 * les boutons pleins de l'application. Un filtre de luminosité multiplie les
 * trois canaux : il désature autant qu'il assombrit, et donne sur l'orange
 * #be4620 un brun terne qui n'appartient à aucune palette — le survol se
 * lisait comme un désengagement plutôt que comme une réaction. Un token de
 * couleur explicite (`--mlc-primary-hover`), réglé pour chaque thème et
 * vérifié au contraste contre `--mlc-primary-foreground`, dit la même chose
 * en restant dans la marque.
 */
export const HOVER_SOLID = 'hover:bg-primary-hover';

/** Survol d'un contrôle à bordure (bouton secondaire, champ, case) : la bordure prend l'accent. */
export const HOVER_OUTLINE = 'hover:border-primary';

/**
 * Survol d'un contrôle sans fond ni bordure (action tertiaire d'une fiche,
 * entrée de navigation) : une surface apparaît sous le curseur. Il manquait,
 * donc une action tertiaire ne réagissait pas du tout, ou se voyait affublée
 * d'une bordure pour être visible — d'où les rangées de boutons cerclés qui
 * donnaient à la fiche recette son allure de barre d'outils.
 */
export const HOVER_QUIET = 'hover:bg-surface-sunken';

/**
 * Désactivé : le contrôle **quitte sa couleur d'action** pour une surface
 * neutre, et n'intercepte plus le pointeur.
 *
 * **Ce n'était pas ce que faisait `disabled:opacity-40`**, et le défaut se
 * voyait sur « Ajouter la semaine à ma liste de courses » (F-10, audit design
 * du 26 août 2026) : un rose délavé illisible. La cause est arithmétique —
 * l'opacité s'applique à l'élément **entier**, si bien que le texte et son
 * fond se rapprochent tous deux de la couleur de la page, et le contraste
 * *interne* s'effondre. Sur le bouton primaire en thème clair, il tombait à
 * **1,31:1**, et même à 80 % d'opacité il ne remonte qu'à 2,90:1 : aucune
 * valeur d'opacité ne rend ce bouton lisible, c'est le mécanisme qui est
 * mauvais. Recolorer, en revanche, garde la maîtrise des deux couleurs —
 * `foreground-muted` sur `surface-sunken` donne 4,68:1 en clair et 6,20:1 en
 * sombre, au-dessus du seuil AA (§4).
 *
 * Un bouton désactivé se distingue donc d'un bouton actif par ce qu'il a
 * **perdu** — sa couleur, son ombre — et non par ce qu'on lui a retiré de
 * lisibilité.
 */
export const DISABLED =
  'disabled:border-border disabled:bg-surface-sunken disabled:text-foreground-muted disabled:shadow-none disabled:pointer-events-none';

/**
 * Désactivé, pour un contrôle dont le signal est une **forme** et non un
 * texte : la case à cocher, les puces retirables. Il n'y a rien à recolorer —
 * la marque *est* le contenu —, donc elle s'estompe. À 50 % elle reste
 * reconnaissable, ce que 40 % ne garantissait pas sur fond clair.
 */
export const DISABLED_SHAPE = 'disabled:opacity-50 disabled:pointer-events-none';

/**
 * En cours : `aria-busy` porte l'information à l'assistance technique, la
 * classe porte le curseur. Le libellé, lui, ne change pas — le remplacer par
 * « Chargement… » fait perdre de vue ce qu'on avait demandé.
 */
export const BUSY = 'aria-busy:cursor-progress aria-busy:opacity-70';

/** Réactions communes à tout contrôle interactif. */
export const INTERACTIVE = `transition ${FOCUS_RING} ${DISABLED} ${BUSY}`;

/**
 * Cible tactile minimale (§4 CLAUDE.md). Deux planchers : 44 px partout,
 * 48 px en mode cuisine et sur tout écran manipulé les mains occupées.
 * `min-h-touch-min` et non une valeur en dur, pour que le test de cibles
 * (client/test/touchTargets.test.tsx) reconnaisse la classe.
 */
export const TOUCH_MIN = 'min-h-touch-min';
export const TOUCH_COOK = 'min-h-touch';
